import { err, ok, type Result } from 'neverthrow';
import type { Graph, NodeId } from '../../../types/graph';
import { OptionType } from '@morph-mapper/types';
import {
  CommandErrorKind,
  CommandKind,
  type CommandResult,
  type CommandError,
  type GraphCommand,
  type EffectBuilder,
} from '../types';
import { effectsForOptionChange, withEffects } from '../effects';
import { bumpGraph } from '../utils';

export class UpdateNodeOptionTypeCommand implements GraphCommand {
  readonly kind = CommandKind.UpdateNodeOptionType;

  constructor(
    public readonly nodeId: NodeId,
    public readonly optionKey: string,
    public readonly type: OptionType,
    public readonly value?: unknown
  ) {}

  apply(graph: Graph): Result<CommandResult, CommandError> {
    const node = graph.nodes[this.nodeId];
    if (!node) {
      return err({
        kind: CommandErrorKind.NotFound,
        message: `Node ${this.nodeId} not found`,
      });
    }

    const current = node.options[this.optionKey];
    if (!current || !('type' in current)) {
      return err({
        kind: CommandErrorKind.NotFound,
        message: `Option ${this.optionKey} not found`,
      });
    }

    let previousValue = null;
    if (current.type === OptionType.Graph) {
      previousValue = current.graphId;
    } else {
      previousValue = current.value;
    }

    const nextOption =
      this.type === OptionType.Graph
        ? {
            type: this.type,
            graphId: typeof this.value === 'string' ? this.value : undefined,
            visibility: current.visibility,
          }
        : {
            type: this.type,
            value: this.value ?? null,
            visibility: current.visibility,
          };

    const nextGraph: Graph = {
      ...graph,
      nodes: {
        ...graph.nodes,
        [this.nodeId]: {
          ...node,
          options: {
            ...node.options,
            [this.optionKey]: nextOption,
          },
        },
      },
    };

    return ok({
      graph: bumpGraph(nextGraph),
      inverse: new UpdateNodeOptionTypeCommand(
        this.nodeId,
        this.optionKey,
        current.type,
        previousValue
      ),
      dirty: {
        nodes: [this.nodeId],
        edges: [],
        topologyChanged: false,
      },
    });
  }
}

export const createUpdateNodeOptionTypeCommand = (
  nodeId: NodeId,
  optionKey: string,
  type: OptionType,
  value?: unknown
) => {
  const command = new UpdateNodeOptionTypeCommand(
    nodeId,
    optionKey,
    type,
    value
  );
  const buildEffects: EffectBuilder = ({ before, after }) => {
    const beforeOption = before.nodes[nodeId]?.options[optionKey];
    const afterOption = after.nodes[nodeId]?.options[optionKey];
    return effectsForOptionChange(beforeOption, afterOption);
  };

  return withEffects(command, buildEffects, ({ inverse }) =>
    withEffects(inverse, buildEffects)
  );
};
